import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PICKER_PAGE_SIZE } from './pagination.js';
import { IsoDateSchema } from './primitives.js';
const c = initContract();
/**
 * GEN-12 — the top bar's global search, which the prototype draws and wires to
 * nothing: *"The search box must search across customers, vendors, items and
 * documents, and must show results after 2 typed characters."*
 *
 * §4.3.2's results panel names five groups, in a fixed order — Items, Business
 * partners, Documents, Accounts, Employees — each row "a type icon, a bold
 * primary line, the record's code in monospace at the trailing edge, and one
 * grey secondary line" (GEN-12d: "Every result row must carry the record's
 * code, so two records with the same name can be told apart").
 *
 * ── ONE ENDPOINT, PERMISSION-SHAPED (GEN-12a) ──────────────────────────────
 *
 * *"The results panel must show only records the signed-in user's role permits
 * them to open. A hidden record must not appear even as a count."* So a group
 * the caller may not open is ABSENT from the response — not present with zero,
 * not greyed (the 16.7 row-1 shape: absent, never greyed). An absent group is
 * therefore deliberately indistinguishable from a group with no matches: the
 * response may not leak which of the two it was.
 *
 * ── WHY THE ROWS ARE STRUCTURED, NOT PRE-COMPOSED ──────────────────────────
 *
 * The secondary line is localized text ("Supplier · owes 4,200.00" reads in
 * Arabic for an Arabic reader). A server-composed sentence would be composed
 * in one language, so each row carries the FIELDS §4.3.2's table names for its
 * type and the client composes the line with the reader's own locale — the
 * same split every list screen already makes.
 */
/** GEN-12: "must show results after 2 typed characters". */
export const SEARCH_MIN_CHARS = 2;
/** GEN-12b: "must wait 250 milliseconds after typing stops". */
export const SEARCH_DEBOUNCE_MS = 250;
/**
 * §4.3.2: "**See all N results** … shown only when more than 5 results exist
 * in any group" — so the PANEL draws at most five rows per group.
 */
export const SEARCH_PANEL_GROUP_ROWS = 5;
/**
 * The Documents group's members: every partner-carrying document type — the
 * §4.3.2 row shape is "The document type and number" over "The partner · the
 * document date", which is exactly the set below. Journal entries, stock
 * vouchers, transfers and work orders carry no partner and are reachable
 * through their own screens; they are deliberately not in the group.
 */
export const SEARCH_DOCUMENT_KINDS = [
    'quotation',
    'sales_order',
    'invoice',
    'delivery_note',
    'credit_note',
    'purchase_order',
    'goods_receipt',
    'purchase_bill',
    'purchase_return',
    'receipt',
    'payment_voucher',
];
/** GEN-12d: every hit carries id (to open it), code (to tell twins apart), title. */
const hitBase = {
    id: z.uuid(),
    /** The printed code at the trailing edge — ITM-3045, BP-2260, INV-2042. */
    code: z.string(),
    /** The bold primary line: the record's name (documents add their type label client-side). */
    title: z.string(),
};
export const SearchHitSchema = z.discriminatedUnion('type', [
    /** Items: "Category · price · on-hand quantity". */
    z
        .object({
        ...hitBase,
        type: z.literal('item'),
        category: z.string().nullable(),
        price: z.string(),
        onHand: z.string(),
    })
        .strict(),
    /** Business partners: "Their types · what they owe or are owed". */
    z
        .object({
        ...hitBase,
        type: z.literal('partner'),
        isCustomer: z.boolean(),
        isSupplier: z.boolean(),
        /** Signed: positive = they owe us, negative = we owe them. */
        owed: z.string(),
    })
        .strict(),
    /** Documents: "The partner · the document date". */
    z
        .object({
        ...hitBase,
        type: z.literal('document'),
        kind: z.enum(SEARCH_DOCUMENT_KINDS),
        partner: z.string().nullable(),
        date: IsoDateSchema,
    })
        .strict(),
    /** Accounts: "Its code and its balance". */
    z
        .object({
        ...hitBase,
        type: z.literal('account'),
        balance: z.string(),
    })
        .strict(),
    /** Employees: "Their job title and department". */
    z
        .object({
        ...hitBase,
        type: z.literal('employee'),
        jobTitle: z.string(),
        jobTitleAr: z.string(),
        department: z.string(),
    })
        .strict(),
]);
/**
 * One group: the heading count and the top rows. `total` is the full match
 * count for the heading and the "See all N results" arithmetic; `rows` is
 * bounded by `perGroup` — a panel, never a page (PICKER_PAGE_SIZE's rule).
 */
export const SearchGroupSchema = z
    .object({
    total: z.number().int().nonnegative(),
    rows: SearchHitSchema.array(),
})
    .strict();
/**
 * The five groups of §4.3.2, in its order. EVERY key optional: a group is
 * absent when the caller's role may not open that record type (GEN-12a) — and
 * equally when nothing matched, so absence never says which.
 */
export const SearchResponseSchema = z
    .object({
    items: SearchGroupSchema.optional(),
    partners: SearchGroupSchema.optional(),
    documents: SearchGroupSchema.optional(),
    accounts: SearchGroupSchema.optional(),
    employees: SearchGroupSchema.optional(),
})
    .strict();
export const SEARCH_GROUP_ORDER = [
    'items',
    'partners',
    'documents',
    'accounts',
    'employees',
];
export const searchContract = c.router({
    /**
     * The one search. `q` is trimmed and must reach GEN-12's two characters —
     * the panel never asks below that, and the server refuses rather than
     * scans on one letter. `perGroup` is the row bound per group: the panel
     * asks for 5 (SEARCH_PANEL_GROUP_ROWS), the full results screen for
     * PICKER_PAGE_SIZE, and nothing may ask for more.
     */
    query: {
        method: 'GET',
        path: '/search',
        query: z
            .object({
            q: z.string().trim().min(SEARCH_MIN_CHARS).max(120),
            perGroup: z.coerce
                .number()
                .int()
                .min(1)
                .max(PICKER_PAGE_SIZE)
                .default(SEARCH_PANEL_GROUP_ROWS),
        })
            .strict(),
        responses: { 200: SearchResponseSchema },
        summary: 'GEN-12 — the top bar global search, grouped and permission-shaped',
    },
}, { pathPrefix: '/api' });
//# sourceMappingURL=search.js.map